const getEnvKey = () => typeof process !== 'undefined' ? process.env.GEMINI_API_KEY : undefined;
import { jsonrepair } from "jsonrepair";
export async function checkApiKeyStatus() {
  try {
    // Check if we have a key in the frontend environment
    const frontendKey = getEnvKey();
    
    let absoluteUrl = "/api/config";
    if (typeof window === 'undefined') {
      const port = 3000;
      absoluteUrl = `http://127.0.0.1:${port}${absoluteUrl}`;
    }
    const response = await fetch(absoluteUrl);
    if (response.ok) {
      const config = await response.json();
      
      return {
        geminiPresent: !!frontendKey || (!!config.geminiPresent && !config.geminiIsFallback),
        geminiSource: frontendKey ? "frontend-env" : (config.geminiSource || "unknown"),
        geminiIsFallback: !frontendKey && !!config.geminiIsFallback,
        fmpPresent: !!config.fmpPresent,
        isProduction: !!config.isProduction
      };
    }
  } catch (e) {
    console.error("Failed to check API key status:", e);
  }
  return { 
    geminiPresent: !!getEnvKey(), 
    geminiSource: "error", 
    geminiIsFallback: false,
    fmpPresent: false,
    isProduction: false 
  };
}
export function extractText(response: any): string {
  if (!response) return "";
  
  try {
    // Check if it's a GenerateContentResponse with candidates
    if (response.candidates && response.candidates.length > 0) {
      const candidate = response.candidates[0];

      // Check for safety filters or other non-complete reasons
      if (candidate.finishReason && candidate.finishReason !== "STOP" && candidate.finishReason !== "MAX_TOKENS" && candidate.finishReason !== "TOOL_USE") {
         console.warn(`[Gemini] Response finishReason: ${candidate.finishReason}. This might be a safety block or truncation.`);
      }

      if (candidate.content && candidate.content.parts) {
        // Filter out reasoning/thought parts to avoid breaking JSON parsers
        const textParts = candidate.content.parts
          .filter((p: any) => p.text && !p.thought && !p.reasoning)
          .map((p: any) => {
            // Also strip <thought> and <think> tags if they are embedded in a text part
            return p.text.replace(/<(?:thought|think)>[\s\S]*?<\/(?:thought|think)>/gi, "").trim();
          });
          
        if (textParts.length > 0) {
          return textParts.join("");
        }
      }
    }

    // Attempt to use the standard text property as a last resort
    // Use try-catch because .text is a getter that throws if parts aren't compatible
    try {
      const sdkText = response.text;
      if (typeof sdkText === "string" && !sdkText.includes("there are non-text parts")) {
        return sdkText;
      }
    } catch {
      // Ignore SDK text retrieval failures
    }
  } catch (e) {
    console.error("[Gemini] Error extracting text from response:", e);
  }

  return "";
}
export function cleanJson(text: string): string {
  if (!text) return "";
  
  // Remove reasoning blocks from reasoning models if present
  let clean = text.replace(/<(?:thought|think)>[\s\S]*?<\/(?:thought|think)>/gi, "").trim();
  
  // If the cleaning removed everything, maybe the model only returned reasoning
  if (!clean && (text.includes("<thought>") || text.includes("<think>"))) {
     console.warn("[Gemini] Model only returned reasoning, no content found.");
     return "";
  }

  // Remove markdown wrap
  let cleaned = clean.replace(/```(?:json)?\s*/gi, "").trim();

  // Find the first { or [ to remove prefix text
  const firstBrace = cleaned.indexOf('{');
  const firstBracket = cleaned.indexOf('[');
  let startIdx = -1;
  if (firstBrace !== -1 && firstBracket !== -1) {
    startIdx = Math.min(firstBrace, firstBracket);
  } else if (firstBrace !== -1) {
    startIdx = firstBrace;
  } else if (firstBracket !== -1) {
    startIdx = firstBracket;
  }

  if (startIdx > 0) {
    cleaned = cleaned.substring(startIdx);
  }

  // 1. Try parsing immediately
  try {
    JSON.parse(cleaned);
    return cleaned; // It's already valid!
  } catch (e) {
    // ignore
  }

  // 2. Try jsonrepair immediately
  try {
    return jsonrepair(cleaned);
  } catch (err) {
    // Silently ignore to try fallback recovery strategies
  }

  // 3. Check for trailing text / unescaped quotes causing brace mismatch
  // We'll use a manual stack to find the end of the JSON.
  let endIdx = -1;
  let parsedStack: string[] = [];
  let inStringVar = false;
  let escapedVar = false;

  for (let i = 0; i < cleaned.length; i++) {
    const char = cleaned[i];
    if (inStringVar) {
      if (char === '\\' && !escapedVar) escapedVar = true;
      else if (char === '"' && !escapedVar) inStringVar = false;
      else escapedVar = false;
    } else {
      if (char === '"') inStringVar = true;
      else if (char === '{') parsedStack.push('}');
      else if (char === '[') parsedStack.push(']');
      else if (char === '}' || char === ']') {
        parsedStack.pop();
        if (parsedStack.length === 0) {
          endIdx = i;
          break;
        }
      }
    }
  }

  if (endIdx !== -1) {
    cleaned = cleaned.substring(0, endIdx + 1);
  } else {
    // Fallback if truncated
    const isBrace = cleaned.startsWith('{');
    const fallbackIdx = isBrace ? cleaned.lastIndexOf('}') : cleaned.lastIndexOf(']');
    if (fallbackIdx > 0) {
      cleaned = cleaned.substring(0, fallbackIdx + 1);
    }
  }

  // 4. Try parsing again after truncation
  try {
    JSON.parse(cleaned);
    return cleaned; // It's valid!
  } catch (e) {
    // ignore
  }

  // Fix unescaped newlines in strings (common issue with LLM JSON)
  cleaned = cleaned.replace(/(?<=: *")([^"\\]*(?:\\.[^"\\]*)*)(?=")/g, (match) => {
    return match.replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t');
  });

  try {
    return jsonrepair(cleaned);
  } catch (err) {
    // Silently ignore to try final severe truncation strategy
  }

  // Basic repair for severely truncated JSON
  let inString = false;
  let escaped = false;
  let stack: string[] = [];

  for (let i = 0; i < cleaned.length; i++) {
    const char = cleaned[i];

    if (char === '\\' && !escaped) {
      escaped = true;
    } else if (char === '"' && !escaped) {
      inString = !inString;
      escaped = false;
    } else if (!inString) {
      if (char === '{') stack.push('}');
      else if (char === '[') stack.push(']');
      else if (char === '}' || char === ']') stack.pop();
      escaped = false;
    } else {
      escaped = false;
    }
  }

  if (inString) {
    if (escaped) {
      cleaned = cleaned.slice(0, -1);
    }
    cleaned += '"';
  }

  // If it ends with a colon, it was about to start a value. Provide a dummy value.
  if (cleaned.match(/:\s*$/)) {
    cleaned += 'null';
  }

  // Remove trailing commas before closing
  cleaned = cleaned.replace(/,\s*$/, '');

  // Pop remaining expected closing characters from the stack
  while (stack.length > 0) {
    cleaned += stack.pop();
  }

  try {
    return jsonrepair(cleaned);
  } catch (err) {
    // Return best effort and let the caller's JSON.parse throw and trigger withRetry
    return cleaned;
  }
}
// [S6] Optional per-call context so the retry wrapper can name the agent/model
// that parse-failed and notify a per-job counter. Fully backward compatible:
// when omitted, behaviour is unchanged except the log gains the attempt number.
export interface RetryContext {
  agent?: string;
  model?: string;
  onJsonParseRetry?: (info: {
    agent?: string;
    model?: string;
    chars: number;
    attempt: number;
  }) => void;
}
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 4,
  ctx?: RetryContext,
): Promise<T> {
  let lastError: any;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      // [KEEP2] Our own proxy rate limit is non-retryable — retrying only burns
      // the window. Fail fast; the UI surfaces retryAfterSeconds.
      if (error?.code === 'GEMINI_RATE_LIMIT') {
        throw error;
      }

      // Extract error details safely
      const errorStr = typeof error === 'string' ? error : (error instanceof Error ? error.stack || error.message : JSON.stringify(error));
      const errorMsg = error?.message || error?.error?.message || "";
      const combinedMsg = `${errorStr} ${errorMsg}`.toLowerCase();
      const statusCode = error?.status || error?.error?.code || error?.code || 0;
      
      // Check for transient errors that should be retried
      const isRateLimit = combinedMsg.includes("429") || 
                          statusCode === 429 || 
                          statusCode === "429" ||
                          combinedMsg.includes("resource_exhausted") ||
                          combinedMsg.includes("quota") ||
                          combinedMsg.includes("rate exceeded");
      
      const isServiceUnavailable = combinedMsg.includes("503") || 
                                   statusCode === 503 || 
                                   statusCode === "503" ||
                                   combinedMsg.includes("unavailable") ||
                                   combinedMsg.includes("high demand") ||
                                   combinedMsg.includes("overloaded") ||
                                   combinedMsg.includes("unexpected content-type") ||
                                   combinedMsg.includes("starting server") ||
                                   combinedMsg.includes("deadline_exceeded") ||
                                   combinedMsg.includes("timeout") ||
                                   combinedMsg.includes("internal_error") ||
                                   combinedMsg.includes("500") ||
                                   statusCode === 500 ||
                                   statusCode === "500" ||
                                   combinedMsg.includes("service_unavailable") ||
                                   combinedMsg.includes("504") || 
                                   statusCode === 504 ||
                                   statusCode === "504" ||
                                   combinedMsg.includes("etimedout") ||
                                   ((statusCode === 500 || statusCode === "500" || combinedMsg.includes("500") || error?.status === "Internal Server Error") && 
                                   (combinedMsg.includes("rpc failed") || combinedMsg.includes("xhr error") || combinedMsg.includes("error code: 6") || combinedMsg.includes("internal server error")));
      
      const isTransient = isRateLimit || isServiceUnavailable || error instanceof SyntaxError || 
                          combinedMsg.includes("token") || combinedMsg.includes("limit") || combinedMsg.includes("finish_reason: 4") ||
                          combinedMsg.includes("unexpected content-type") || combinedMsg.includes("starting server") ||
                          combinedMsg.includes("oracle-c-gate");
      
      if (isTransient && i < maxRetries - 1) {
        // Less aggressive backoff for high demand so we can respond faster
        const baseDelay = (isServiceUnavailable || error instanceof SyntaxError) ? 5000 : 3000;
        const delay = Math.min((Math.pow(1.5, i) * baseDelay + Math.random() * 2000), 30000);
          const reason = isRateLimit ? "rate limited" : (error instanceof SyntaxError ? "JSON parse error" : "experiencing high demand or timeout");
          if (error instanceof SyntaxError) {
             // [S6] Retain the "Suppressed JSON parse failure" marker; augment with
             // the offending agent/model and the attempt number, and notify the
             // per-job counter so the retry rate is queryable across the corpus.
             const who = ctx?.agent
               ? `${ctx.agent}${ctx.model ? "/" + ctx.model : ""}: `
               : "";
             console.warn(`[Retry] ${who}Suppressed JSON parse failure (${errorStr.length} chars), attempt ${i + 1} - retrying...`);
             try {
               ctx?.onJsonParseRetry?.({ agent: ctx?.agent, model: ctx?.model, chars: errorStr.length, attempt: i + 1 });
             } catch { /* counter must never break the retry loop */ }
          } else {
             console.warn(`[Retry] API ${reason}: ${errorMsg || statusCode}. Retrying in ${Math.round(delay/1000)}s... (Attempt ${i + 1}/${maxRetries})`);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
      }
      throw error;
    }
  }
  throw lastError;
}
